import ErrorCode from '../../common/error-code';
import { generateErrorMessage } from '../../common/error-generator';
import { ErrorName } from '../../common/error-info';
import RtcError from '../../common/rtc-error';
import innerEmitter from '../../inner-emitter/inner-emitter';
import INNER_EVENT from '../../inner-emitter/inner-event';
import { ProcessHandler, createDecorator } from './middleware';

/**
 * 装饰器 - 限制每秒调用频次
 *
 * @param {Number} timesInSecond 每秒调用次数
 * @param {Number} maxSizeInSecond 接口参数每秒 maxSize。例如：sendSEIMessage 每秒最多发送8KB数据，此处则填 8000
 * @param {Function} getSize 获取当前调用的参数 size。
 */
export function limitCallFrequency({ timesInSecond, maxSizeInSecond, getSize }: { timesInSecond: number, maxSizeInSecond: number, getSize: (...args: any) => number; }) {
  return createDecorator((next: ProcessHandler<any>, name: string) => {
    // 用于处理多 Client 场景，多 Client 不共享调用次数限制。
    const map = new WeakMap();
    // client 实例销毁时，释放 client 实例引用
    innerEmitter.on(INNER_EVENT.ROOM_DESTROY, ({ room }) => map.delete(room));
    return function (...args: any) {
      let value = map.get(this);
      if (!value) {
        value = {
          callCountInSecond: 0,
          timestamp: 0,
          totalSizeInSecond: 0
        };
        map.set(this, value);
      }
      // 初次调用接口
      if (value.timestamp === 0) {
        value.timestamp = Date.now();
      } else if (Date.now() - value.timestamp > 1000) {
        // 时间已超过 1s，重置调用次数、时间戳、totalSize
        value.timestamp = Date.now();
        value.callCountInSecond = 0;
        value.totalSizeInSecond = 0;
      }
      if (getSize) {
        value.totalSizeInSecond += getSize(...args);
      }
      if (
        value.timestamp !== 0 &&
        Date.now() - value.timestamp < 1000 &&
        // 1s 内调用次数超过预设值，直接 return，不执行
        (value.callCountInSecond >= timesInSecond || value.totalSizeInSecond > maxSizeInSecond)
      ) {
        throw new RtcError({
          code: ErrorCode.INVALID_OPERATION,
          message: generateErrorMessage({
            key: ErrorName.CALL_FREQUENCY_LIMIT,
            data: {
              isTimes: value.callCountInSecond >= timesInSecond,
              isSize: value.totalSizeInSecond > maxSizeInSecond,
              name,
              timesInSecond,
              maxSizeInSecond
            }
          })
        });
      }

      value.callCountInSecond++;
      next.call(this, ...args);
    };
  });
}
